import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SampleDashboardDto } from '@sharedcommon/model/sample/sample-dashboard.model';
import { PrimengTableService } from '@sharedcommon/components/primeng-table/primeng-table.service';
import { ActionTableEvent } from '@sharedcommon/model/action-table-event.model';
import {HeaderService} from '@sharedcommon/services/header.service';
import { UserService } from '@sharedcommon/services/user.service';
import { RawMaterialOrSampleUtil } from '@sharedcommon/util/rawmaterial-or-sample.util';
import { Rights } from '@sharedcommon/util/rights.util';
import { OperatingModeUtil } from '@sharedcommon/util/operating-mode.util';
import { PrimengTableCols, SortOrderTableEnum } from '@sharedcommon/interfaces/primeng-table-cols';
import { SampleRestService } from '@sharedcommon/rest/sample/sample.restservice';
import { Observable, Subject, lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RawmaterialOrSampleStatus } from '@sharedcommon/util/rawmaterial-or-sample-status.utils';
import { NeqTableComponent } from '@sharedcommon/components/table/neq-table.component';
import { NeqDialogService } from '@sharedcommon/services/dialog.service';
import { ToastService } from '@sharedcommon/services/toast.service';
import { NeqTableRequest } from '@sharedcommon/components/table/neq-table-request.model';
import { SampleFilterDto } from '@sharedcommon/model/sample/filter/sample-filter.model';
import { NeqTableData } from '@sharedcommon/model/neq-table-data.model';
import { SortOrderUtil } from '@sharedcommon/util/sort-order.util';
import { CalculationStatusService } from '@usercommon/service/calculation-status.service';
import { CalculationStatus } from '@sharedcommon/model/calculation-status.model';
import { HttpStatusCode } from '@angular/common/http';
import { SampleAdvancedSearchCriterias } from '@sharedcommon/model/sample-advanced-search-criterias.model';
import { SampleAdvancedSearchService } from '@usercommon/service/sample-advanced-search.service';
import { Router } from '@angular/router';
import { FunctionUtils } from '@sharedcommon/util/functions.util';
import { RmOrSampleMultiModificationStore } from '@usercommon/stores/rm-or-sample-multi-modification.store';
import { DmsAndSampleService } from '@usercommon/service/dms-and-sample.service';
import { SampleService } from '@usercommon/service/sample.service';

@Component({
  selector: 'neq-samples-list',
  templateUrl: './samples-list.page.html'
})
export class NeqSamplesListPage implements OnInit, OnDestroy {
  @ViewChild(NeqTableComponent) table: NeqTableComponent<any>;

  /**
   * Column configuration of the prime ng table
   */
  cols: PrimengTableCols[] ;

  sessionIdOfTable: string = "sampleList";
  displayArchived: boolean = false;

  advanceSearchCriterias: SampleAdvancedSearchCriterias;

  createRight = [ Rights.U_SAMPLE_CREATE ];

  private checkedSamplesMultiModif: SampleDashboardDto[] = [];

  private readonly destroy$ = new Subject();
  public showTable = true;


  constructor(
    private readonly headerService: HeaderService,
    private readonly dialogService: NeqDialogService,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly sampleRestService: SampleRestService,
    private readonly sampleService: SampleService,
    private readonly primeTableService: PrimengTableService,
    private readonly advancedSearchService: SampleAdvancedSearchService,
    private readonly toastService: ToastService,
    private readonly dmsAndSampleService: DmsAndSampleService,
    private readonly primeNgTableService: PrimengTableService,
    private readonly rmOrSampleMultiSelectionStore: RmOrSampleMultiModificationStore
  ) {
    this.cols = [
        {
          field: 'access', header: 'label.common.access', iconHeaderClassName: 'fas fa-lock',
          type: 'lock', tooltipMsgKey: 'lockUserFullName', sortable: false, filterable: false, position: 'center'
        },
        {
          field: 'calculationStatus', header: 'label.user.rawmaterial.calculation-status',
          iconHeaderClassName: 'fas fa-calculator', type: 'calculationIcon', position: 'left',
          filterOptions: [
            {label: this.translateService.instant('label.common.all'), value: null},
            {
              label: this.translateService.instant(CalculationStatusService.returnCalculationStatusKeyMsg(CalculationStatus.BlockAfterCalc)),
              value: CalculationStatus.BlockAfterCalc
            },
            {
              label: this.translateService.instant(CalculationStatusService.returnCalculationStatusKeyMsg(CalculationStatus.BlockBeforeCalc)),
              value: CalculationStatus.BlockBeforeCalc
            },
            {
              label: this.translateService.instant(CalculationStatusService.returnCalculationStatusKeyMsg(CalculationStatus.Valid)),
              value: CalculationStatus.Valid
            }
          ]
        },
        { field: 'code', header: 'label.common.code' },
        { field: 'label', header: 'label.common.label' },
        { field: 'farm', fieldLvl2: "label", forcedFieldIdentifier: "farmLabel", header: 'label.common.farm-name' },
        { field: 'validationDate', header: 'label.common.validation', type:'date' },
        { field: 'technicianName', header: 'label.common.technician' },
        { field: 'entityLabel', header: 'label.common.entity', sortable: false},
        { field: 'referenceLabel', header: 'label.common.reference', sortable: false },
        { field: 'referencePredictionModelLabel', header: 'label.common.prediction-model', sortable: false },
        { field: 'calculationDate', header: 'label.common.last-record', type: 'date', defaultSortOrder: SortOrderTableEnum.desc, defaultSortPosition: 1 },
        { field: 'calculationBy', header: 'label.common.by' },
        {
          field: null,
          header: 'label.common.pdf',
          type: 'extendedActions',
          position: 'center',
          actions: [
            {
              type: 'pdf',
              class:'squared action important',
              right: null,
              svgIcon: 'pdf',
              tooltip: "msg.icon.sample-list.pdf",
              /* Show the pdf button on when the bulletin dms id exist */
              showAction: (sample: SampleDashboardDto) => !!sample.bulletinDmsId
            }
          ]
        },
        {
          field: null,
          header: 'ACTIONS',
          type: 'extendedActions',
          position: 'left',
          actions: [
            {
              type: 'edit', class:'action important',
              svgIcon: 'editer', tooltip: "msg.common.icon.sample.edit"
            },
            {
              type: 'disable', class:'action', right: this.getRightsAccordingActivity([Rights.A_MP_SAMPLE_ENABLE], [Rights.A_FODDER_SAMPLE_ENABLE]),
              svgIcon: 'activer', tooltip: "msg.common.icon.sample.disable",
              // Hide action because not specified yet
              showAction: (_) =>false  //, showAction: RawMaterialOrSampleUtil.isEnabled,
            },
            {
              type: 'enable', class:'action',  right: this.getRightsAccordingActivity([Rights.A_MP_SAMPLE_ENABLE], [Rights.A_FODDER_SAMPLE_ENABLE]),
              svgIcon: 'desactiver', tooltip: "msg.common.icon.sample.enable",
              // Hide action because not specified yet
              showAction: (_) => false //showAction: RawMaterialOrSampleUtil.canBeEnabled,
            },
            {
              type: 'archive', class:'action', right: this.getRightsAccordingActivity([Rights.A_MP_SAMPLE_ARCHIVE], [Rights.A_FODDER_SAMPLE_ARCHIVE]),
              svgIcon: 'archiver', tooltip: "msg.common.icon.sample.archive",
              // Hide action because not specified yet
              showAction: (_) => false, // showAction: RawMaterialOrSampleUtil.isNotArchived,
            },
            {
              type: 'unarchive', class:'action', right: this.getRightsAccordingActivity([Rights.A_MP_SAMPLE_DESARCHIVE], [Rights.A_FODDER_SAMPLE_DESARCHIVE]),
              svgIcon: 'desarchiver', tooltip: "msg.common.icon.sample.desarchive",
              // Hide action because not specified yet
              showAction: (_) => false //showAction: RawMaterialOrSampleUtil.isArchived,
            }
          ]
        }
    ];
  }

  ngOnInit(): void {
    this.rmOrSampleMultiSelectionStore.stop();
    this.updateHeaders('');
    this.primeTableService.actionEvent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((actionEvent: ActionTableEvent) => {
        if (actionEvent) {
          this.treatAction(actionEvent);
          this.primeTableService.setActionEvent(null);
        }
      });
    this.advancedSearchService.getCurrentAdvancedSearch()
      .pipe(takeUntil(this.destroy$))
      .subscribe(criterias => {
        this.advanceSearchCriterias = criterias;
        if (this.table) {
          this.table.fireRefresh();
        }
      });
    this.primeNgTableService.selectRowEvent$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedRows => this.checkedSamplesMultiModif = selectedRows ?? []);
  }

  leaveAdvancedSearchResult(): void {
    /** Will trig the subscription done in ngOnInit and will reset the table */
    this.advancedSearchService.setCurrentAdvancedSearch(null);
  }

  /**
   * Reset all filter in the table
   */
  resetFilter() {
    this.primeTableService.setResetTableEvent(this.sessionIdOfTable);

      this.showTable = false;
      setTimeout(() => {
        this.showTable = true;
      }, 0);
    }

  /**
   * Toggle the archived filtre option and update the table
   */
  displayArchivedSamples() {
    this.displayArchived = !this.displayArchived;
    this.table.fireRefresh();
  }

  /**
   * Function called by the table to refresh its datas
   * @param request the request from the table
   * @returns       the data to displayed
   */
  refreshTable = async (request: NeqTableRequest) => {
    const filterDto = new SampleFilterDto();
    filterDto.page = request.pageIndex;
    filterDto.count = request.nbElementsParPage;
    this.fillFilterDtoOptions(request, filterDto);
    filterDto.sort = SortOrderUtil.returnSortDtoFromTableRequest(request);
    const pagedResultResults = await lastValueFrom(
      this.sampleRestService.searchSampleDashboardByEntityCode(this.userService.getEntity(), filterDto, true)
    );
    const neqTableData = new NeqTableData<SampleDashboardDto>();
    neqTableData.results = pagedResultResults.data;
    neqTableData.totalElements = pagedResultResults.total;
    this.updateHeaders(pagedResultResults.total);
    return neqTableData;
  };

  /**
   * Fill the sample filter DTO options according the table request and advance search
   * @param tableRequest  the request from the table
   * @param filterDto     the filtre sample DTO to fill according the table request
   */
  fillFilterDtoOptions(tableRequest: NeqTableRequest, filterDto: SampleFilterDto): void {
    if (tableRequest.filters) {
      for(const tableFilterParam of tableRequest.filters) {
        switch (tableFilterParam.key) {
          case "calculationDate":
            filterDto.startCalculation = FunctionUtils.convertDateToIsoFormat(tableFilterParam.value[0]);
            filterDto.endCalculation = FunctionUtils.convertDateToIsoFormat(tableFilterParam.value[1]);
            break;
          case "validationDate":
            filterDto.startValidation = FunctionUtils.convertDateToIsoFormat(tableFilterParam.value[0]);
            filterDto.endValidation = FunctionUtils.convertDateToIsoFormat(tableFilterParam.value[1]);
            break;
          default:
            /* The default filter take the key and the value from the table filter */
            filterDto[tableFilterParam.key] = tableFilterParam.value;
            break;
        }
      }
    }
    SampleAdvancedSearchService.fillSampleFilterAccordingCriteriasForSample(filterDto, this.advanceSearchCriterias, true);
    filterDto.archived = this.displayArchived;
  }

  /**
   * Processes the action passed in the emitter according to its type, applies it to the line passed as a parameter
   * @param actionOnRow the action table event
   */
  treatAction(actionOnRow: ActionTableEvent) {
    const sampleDashboard: SampleDashboardDto = actionOnRow.row;
    switch(actionOnRow.type) {
      case 'pdf':
        this.exportPdf(actionOnRow.row);
        break;
      case 'archive':
        this.showPopinAndTrigUpdateStatus(
          sampleDashboard, RawmaterialOrSampleStatus.ARCHIVED, 'msg.user.sample.archived',
          'label.user.sample.archive.confirmation', { param : sampleDashboard.code + ' - ' + sampleDashboard.label });
        break;
      case 'unarchive':
        this.trigUpdateSampleStatus(sampleDashboard, RawmaterialOrSampleStatus.DISABLED, 'msg.user.sample.desarchived');
        break;
      case 'disable':
        this.trigUpdateSampleStatus(sampleDashboard, RawmaterialOrSampleStatus.DISABLED, 'msg.user.sample.disabled');
        break;
      case 'enable':
        this.trigUpdateSampleStatus(sampleDashboard, RawmaterialOrSampleStatus.ENABLED, 'msg.user.sample.enabled');
        break;
      case 'edit':
        this.editSample(actionOnRow.row);
        break;
      default:
        console.error("[NeqSamplesListPage] Event type not handled : ", actionOnRow.type);
        break;
    }
  }

  exportPdf(sampleDashboard: SampleDashboardDto): void {
    this.dmsAndSampleService.downloadDocumentAndCreateEvent(sampleDashboard.bulletinDmsId, sampleDashboard, sampleDashboard.referenceLabel).subscribe();
  }

  editSample(sampleDashboard: SampleDashboardDto): void {
    this.router.navigateByUrl(`/app/samples/input/${sampleDashboard.id}`);
  }

  /**
   * Show confirmation pop-in and trig the update status process
   * @param sampleDashboardDto  the sample dashboard which need to be update
   * @param newStatusValue      the new status value to take
   * @param successKeyMsg       the success message key to translate
   * @param confirmationKeyMsg  the confirmation msg key to translate
   * @param translateParam      the parameter of tranlation for the confirmation pop-in
   */
  showPopinAndTrigUpdateStatus(
    sampleDashboardDto: SampleDashboardDto,
    newStatusValue: RawmaterialOrSampleStatus,
    successKeyMsg: string,
    confirmationKeyMsg: string,
    translateParam: any
  ): void {
    const msg = this.translateService.instant(confirmationKeyMsg, translateParam);
    const asiDialog = this.dialogService.showConfirmation(msg);
    asiDialog.afterClosed().subscribe((result: boolean) => {
      if (!result) {
        return ;
      }
      this.trigUpdateSampleStatus(sampleDashboardDto, newStatusValue, successKeyMsg);
    });
  }

  /**
   * Check if possible to check and update the status of the sample if needed
   * @param sampleDashboardDto  the sample dashboard
   * @param newStatusValue      the new status that must take the sample dashboard
   * @param successKeyMsg       the key to translate in case of success when update the status
   */
  private async trigUpdateSampleStatus(sampleDashboardDto: SampleDashboardDto, newStatusValue: RawmaterialOrSampleStatus, successKeyMsg: string): Promise<void> {
    const statusHasChanged = await lastValueFrom(this.hasStatusChanged(sampleDashboardDto));
    if (!statusHasChanged) {
      this.updateSampleStatus(sampleDashboardDto, newStatusValue, successKeyMsg);
    } else {
      this.showStatusUpdatedErrorMessage();
    }
  }

  /**
   * Update the sample status
   * @param sampleDashboardDto  the sample to update
   * @param newStatusValue      the new status that must take the sample dashboard
   * @param successKeyMsg       the key to translate in case of success when update the status
   */
  updateSampleStatus(sampleDashboardDto: SampleDashboardDto, newStatusValue: RawmaterialOrSampleStatus, successKeyMsg: string): void {
    sampleDashboardDto.status = newStatusValue;
    this.sampleRestService.updateSampleStatus(this.userService.getEntity(), sampleDashboardDto)
      .subscribe({
        next : () => {
          this.toastService.toast(this.translateService.instant(successKeyMsg));
          this.table.fireRefresh();
        },
        error: (error) => {
          if (error.status === HttpStatusCode.Locked) {
            this.showStatusUpdatedErrorMessage();
          } else {
            /* Throw the error to get the built-in error popin */
            throw error;
          }
        }
      });
  }

  /**
   * Check if the status of the sample has been updated
   * @param sampleDashboardDto the sample dashboard which contains the status to check
   * @returns true if the sample has changed
   */
  hasStatusChanged(sampleDashboardDto: SampleDashboardDto): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.sampleRestService.getSampleById(sampleDashboardDto.id)
        .subscribe({
          next : (sampleDto) => {
            if (sampleDto.status !== sampleDashboardDto.status) {
              observer.next(true);
            } else {
              observer.next(false);
            }
            observer.complete();
          },
          error : (error) => {
            observer.next(true);
            observer.complete();
            /* Throw the error to get the built-in error popin */
            throw error;
          }
        });
    })
  }

  /**
   * Update the header of the page
   * @param nbSamples the number of samples which corresponds to the filter
   */
  private updateHeaders(nbSamples: number|string) {
    const strongLabel = '' + nbSamples;
    if (this.userService.isFodderActivityCode()) {
      this.headerService.setHeaderValues({
        strongLabel: strongLabel,
        basicLabel: this.translateService.instant('label.common.fodders'),
        showLogo: true
      });
    } else {
      this.headerService.setHeaderValues({
        strongLabel: strongLabel,
        basicLabel: this.translateService.instant('label.common.rawmaterials') + ' - ' + this.translateService.instant(OperatingModeUtil.returnSampleTranslationKey()),
        showLogo: true
      });
    }
  }

  /**
   * Show popin which indicated that the status of the sample has been updated
   */
  private showStatusUpdatedErrorMessage(): void {
    this.dialogService.showErrorSimpleOneMessage(this.translateService.instant('error.user.sample.status-updated'));
    this.table.fireRefresh();
  }


  /**
   * Return the right accroding the activity of the user
   * @param rawMatRights the right in case of raw materail acticity
   * @param fodderRights the right in case of fodder activity
   * @returns the corresponding rights
   */
  getRightsAccordingActivity(rawMatRights: Rights[], fodderRights: Rights[]): Rights[] {
    return this.userService.isRmActivityCode() ? rawMatRights : fodderRights;
  }

  /**
   * Return the row class of the table
   * @param sampleDashboard
   * @returns the corresponding class
   */
  calculateRowCssClass(sampleDashboard: SampleDashboardDto) {
    if (RawMaterialOrSampleUtil.isDisabled(sampleDashboard)) {
      return 'disabled-bkg-color';
    }
    return '';
  }

  /**
   * Check if the multi selection of modification can be clicked
   *
   * @returns true if the multiselection can be clicked
   */
  canUseMultiselection(): boolean {
    return this.checkedSamplesMultiModif.length > 1;
  }

  /**
   * Check if the user can user the multiselection sample process
   *
   * @returns true if he can do this
   */
  canSelectMulipleSamples(): boolean {
    return this.userService.hasRights([Rights.U_SAMPLE_MODIFY, Rights.A_ENTITY_REFERENT]);
  }

  /**
   * Chech if the sample selection in the table has to be disabled
   *
   * @param sample the sample to check if selectable
   * @returns true if it needs to be disabled
   */
  disableSampleSelection = (sample: SampleDashboardDto) : boolean => {
    return !this.sampleService.canEdit(sample);
  }

  // Multi modification
  onClickMultiselection(): void {
    this.rmOrSampleMultiSelectionStore.start(this.checkedSamplesMultiModif);
  }

  ngOnDestroy(): void {
    this.destroy$.next(false);
    this.destroy$.complete();
  }
}
